import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { fetchAllDropdownListsStarted } from '../../admin/dropdown-lists/store';
import { OpinionScope } from '../../admin/opinions/store';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import styles from './Analytics.module.scss';
import { SingleDataPointChartWrapper } from './charts/SingleDataPointChartWrapper';
import { OpinionAverageChartWrapper } from './charts/average/OpinionAverageChart';
import { JurisdictionCoverageChartWrapper } from './charts/coverage/JurisdictionCoverageChartWrapper';
import { OpinionAnalyticsChart, fetchJurisdictionCoverageStarted, fetchOpinionsAverageStarted, fetchSingleDataJurisdictionStarted, getOpinionAnalyticsScope, getTileInView, setOpinionAnalyticsScope, setOpinionTileInView } from './store';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';

export interface OpinionAnalyticsRouteParams {
    chart: string | undefined;
}

export const OpinionAnalytics: React.FC<RouteComponentProps<OpinionAnalyticsRouteParams>> = ({ match: { params } }) => {
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [tilesDimensions, setTilesDimensions] = useState<{ height: number; width: number; }>({ height: 0, width: 0 });
    const [screenWidth, screenHeight] = useWindowResize();
    const dispatch = useAppDispatch();
    useFetchStarted([fetchSingleDataJurisdictionStarted(), fetchOpinionsAverageStarted(), fetchJurisdictionCoverageStarted(), fetchAllDropdownListsStarted()]);

    const scope = useAppSelector(getOpinionAnalyticsScope);
    const tileInView = useAppSelector(getTileInView);

    const setTileInView = useCallback((chart: OpinionAnalyticsChart | null, shouldRedirect = true) => dispatch(setOpinionTileInView(chart, shouldRedirect)), [dispatch]);

    const scopeOptions = useMemo(() => [
        { value: OpinionScope.ISDA_NETTING, label: 'Netting Opinions (ISDA)' },
        { value: OpinionScope.CLIENT_RELIANCE, label: 'Client Reliance (ISDA)' },
        { value: OpinionScope.COLLATERAL_PROVIDER, label: 'Collateral Opinions (Provider Insolvency)' },
        { value: OpinionScope.COLLATERAL_TAKER, label: 'Collateral Opinions (Taker Insolvency)' },
        { value: OpinionScope.GMRA_NETTING, label: 'Netting Opinions (GMRA)' },
        { value: OpinionScope.GMSLA_NETTING, label: 'Netting Opinions (GMSLA)' }
    ], []);

    const scopeValue = useMemo(() => scopeOptions.find(({ value }) => value === scope) || null, [scopeOptions, scope]);

    const setScope = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value: OpinionScope | null = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value as OpinionScope;
            dispatch(setOpinionAnalyticsScope(value));
        }
    }, [dispatch]);

    const tileInViewChart: JSX.Element | null = useMemo(() => {
        switch (tileInView) {
            case OpinionAnalyticsChart.OPINIONS_AVERAGE:
                return <OpinionAverageChartWrapper dimensions={tilesDimensions!} tileInView={tileInView} setTileInView={setTileInView} analyticsSpinner={showSpinner} />;
            case OpinionAnalyticsChart.SINGLE_DATA_POINT_JURISDICTION:
                return <SingleDataPointChartWrapper dimensions={tilesDimensions!} tileInView={tileInView} setTileInView={setTileInView} analyticsSpinner={showSpinner} />;
            case OpinionAnalyticsChart.JURISDICTION_COVERAGE:
                return <JurisdictionCoverageChartWrapper dimensions={tilesDimensions!} tileInView={tileInView} setTileInView={setTileInView} analyticsSpinner={showSpinner} />;
            default:
                return null;
        }
    }, [tilesDimensions, tileInView, setTileInView, showSpinner]);

    const wideViewChartDimensions = useMemo(() => ({ height: tilesDimensions.height / 2, width: tilesDimensions.width * 0.6 }), [tilesDimensions]);
    const spreadChartDimensions = useMemo(() => ({ height: tilesDimensions.height, width: tilesDimensions.width * 0.4 }), [tilesDimensions]);

    useEffect(() => {
        setShowSpinner(true);
        if (screenWidth && screenHeight) {
            const width = (screenWidth - 80);
            const height = (screenHeight - 190);
            setTilesDimensions({ height, width });
            setTimeout(() => setShowSpinner(false), 50);
        }
    }, [screenWidth, screenHeight, tileInView, setShowSpinner]);

    const allUrlCharts = useMemo(() => Object.values(OpinionAnalyticsChart).map(val => val.replace(' ', '-')), []);
    const tileIsValidAndNotInView = useCallback((urlChart: string) => {
        const urlChartIsValid = allUrlCharts.includes(urlChart);
        if (urlChartIsValid) {
            const chart = urlChart.replaceAll('-', ' ') as OpinionAnalyticsChart;
            return isNull(tileInView) || tileInView !== chart;
        }
    }, [tileInView, allUrlCharts]);

    useEffect(() => {
        if (params.chart && tileIsValidAndNotInView(params.chart)) {
            const chart = params.chart.replaceAll('-', ' ') as OpinionAnalyticsChart;
            setTileInView(chart, false);
        }
    }, [params, tileIsValidAndNotInView, setTileInView]);

    useEffect(() => () => {
        setTileInView(null, false);
    }, [setTileInView]);

    const dropdownSelect = useMemo(() => (
        <div className={styles.scopeDropdown}>
            <Dropdown
                testId='opinion-analytics-scope'
                onChange={setScope}
                value={scopeValue}
                options={scopeOptions}
                isClearable={false}
            />
        </div>
    ), [setScope, scopeValue, scopeOptions]);

    const testId = 'opinion-analytics';

    if (showSpinner) {
        return <PageLoadingDots />;
    }

    return (
        <div className={styles.analyticsWrapper} data-testid={`${testId}-wrapper`}>
            <div className={styles.analyticsHeader}>
                <div className={styles.analyticsTitle} data-testid={`${testId}-header`}>Analytics</div>
                {dropdownSelect}
            </div>
            <div className={styles.analyticsChartsWrapper} data-testid={`${testId}-charts-wrapper`}>
                {isNull(tileInView) ? (
                    <>
                        <div className={styles.leftChartsWrapper}>
                            <div className={styles.wideChartWrapper}>
                                <div className={styles.chartWrapper} style={{ marginBottom: '20px', height: 'calc(100% - 20px)' }}>
                                    <SingleDataPointChartWrapper dimensions={wideViewChartDimensions} analyticsSpinner={showSpinner} tileInView={tileInView} setTileInView={setTileInView} />
                                </div>
                            </div>
                            <div className={styles.wideChartWrapper}>
                                <div className={styles.chartWrapper} style={{ height: '100%' }}>
                                    <JurisdictionCoverageChartWrapper dimensions={wideViewChartDimensions} tileInView={tileInView} setTileInView={setTileInView} analyticsSpinner={showSpinner} />
                                </div>
                            </div>
                        </div>
                        <div className={styles.rightChartsWrapper}>
                            <div className={styles.chartWrapper}>
                                <div className={styles.spreadChartWrapper}>
                                    <OpinionAverageChartWrapper dimensions={spreadChartDimensions} tileInView={tileInView} setTileInView={setTileInView} analyticsSpinner={showSpinner} />
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className={styles.wholePageChartWrapper}>
                        {tileInViewChart}
                    </div>
                )}
            </div>
        </div>
    );
};
